// components/landing/ContactPage.tsx
// ---------------------------------------------------------------------------
// Public contact page for Athena Tech.
// Glassmorphic design consistent with TeamPage and LandingPage.
// Includes a contact form (name, email, message) and company information
// with an embedded Google Maps location.
// ---------------------------------------------------------------------------

import {
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, type FormEvent } from "react";
import api from "../../api/api";
import { useTranslation } from "../../i18n";
import SEOHead from "../common/SEOHead";
import LandingNavBar from "./LandingNavBar";
import { athenaColors } from "./colors";
import LandingFooter from "./sections/LandingFooter";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const COMPANY_INFO = {
  address: "Passatge de Ricard Zamora, 1",
  addressLine2: "Carrer de Can Rabia, 11, Esq",
  city: "08017 Barcelona, Spain",
  phone: "+34 608 666 925",
  email: "support@athenatechai.com",
  linkedin: "https://www.linkedin.com/company/athena-tech-ai",
};

// Google Maps embed URL for the office location
const MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2992.5!2d2.1367!3d41.4055!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12a4a2fad0b0e97d%3A0x6f4e8c1d5e2b3a4c!2sPassatge%20de%20Ricard%20Zamora%2C%201%2C%2008017%20Barcelona!5e0!3m2!1sen!2ses!4v1707000000000!5m2!1sen!2ses";

// ---------------------------------------------------------------------------
// Contact info item
// ---------------------------------------------------------------------------
interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}

function InfoItem({ icon, label, value, href }: InfoItemProps) {
  const theme = useTheme();
  const content = (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
      <Box
        sx={{
          p: 1,
          borderRadius: 2,
          background: alpha(athenaColors.royalBlue, 0.3),
          border: `1px solid ${alpha(athenaColors.periwinkle, 0.15)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: alpha(theme.palette.common.white, 0.5),
            display: "block",
            letterSpacing: 1,
            textTransform: "uppercase",
            fontSize: "0.7rem",
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: alpha(theme.palette.common.white, 0.9),
            fontWeight: 500,
            fontSize: "0.92rem",
            lineHeight: 1.5,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  if (href) {
    return (
      <Box
        component="a"
        href={href}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        sx={{
          textDecoration: "none",
          "&:hover .MuiTypography-body2": {
            color: athenaColors.periwinkle,
          },
          transition: "all 0.2s ease",
        }}
      >
        {content}
      </Box>
    );
  }

  return content;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ContactPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navH = (theme.custom?.navbarHeight as number) || 64;

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  // Navbar callbacks (same pattern as TeamPage)
  const handleSignIn = () => {
    window.location.href = "/";
  };
  const handleNavClick = (href: string) => {
    if (href.startsWith("/")) {
      window.location.href = href;
    } else {
      window.location.href = `/${href}`;
    }
  };

  const handleChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setErrorMsg("");

    try {
      await api.post("/contact/", formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", message: "" });
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const serverMsg = axiosErr?.response?.data?.error;
      setErrorMsg(serverMsg || t("error.generic", "Something went wrong. Please try again."));
    } finally {
      setSending(false);
    }
  };

  // Shared text field styles for dark background
  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      color: theme.palette.common.white,
      backgroundColor: alpha(theme.palette.common.white, 0.04),
      borderRadius: 2,
      "& fieldset": {
        borderColor: alpha(athenaColors.periwinkle, 0.2),
      },
      "&:hover fieldset": {
        borderColor: alpha(athenaColors.periwinkle, 0.4),
      },
      "&.Mui-focused fieldset": {
        borderColor: athenaColors.periwinkle,
      },
    },
    "& .MuiInputLabel-root": {
      color: alpha(theme.palette.common.white, 0.5),
      "&.Mui-focused": {
        color: athenaColors.periwinkle,
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: athenaColors.deepNavy,
        color: theme.palette.common.white,
      }}
    >
      <SEOHead
        title="Contact Us"
        description="Get in touch with Athena Tech. Whether you are exploring partnership opportunities, requesting a demo of our oncology data platform, or have questions about clinical data management, we would love to hear from you."
        path="/contact"
      />
      <LandingNavBar onSignIn={handleSignIn} onNavClick={handleNavClick} />

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: `${navH}px` }} />

      {/* Hero header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          px: { xs: 3, md: 6 },
          textAlign: "center",
          background: `linear-gradient(180deg, ${
            athenaColors.deepNavy
          } 0%, ${alpha(athenaColors.royalBlue, 0.35)} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              display: "block",
              letterSpacing: 4,
              color: athenaColors.periwinkle,
              mb: 2,
              fontSize: "0.8rem",
            }}
          >
            GET IN TOUCH
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: "2rem", md: "2.8rem" },
              background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${athenaColors.periwinkle} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("contact.title", "Contact Us")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: alpha(theme.palette.common.white, 0.8),
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.1rem" },
              maxWidth: 680,
              mx: "auto",
            }}
          >
            {t("contact.description", "We are here to help and answer any questions you might have. Whether you are interested in learning more about Athena, exploring partnership opportunities, or simply want to share your thoughts, we would love to hear from you.")}
          </Typography>
        </Container>
      </Box>

      {/* Main content: Form + Info side by side */}
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 6 },
          }}
        >
          {/* LEFT: Contact Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              background: alpha(theme.palette.common.white, 0.04),
              backdropFilter: "blur(8px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
                color: theme.palette.common.white,
              }}
            >
              {t("contact.send_message_title", "Send us a message")}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: alpha(theme.palette.common.white, 0.55), mb: 4 }}
            >
              {t("contact.form_subtitle", "Fill in the form below and we will get back to you shortly.")}
            </Typography>

            {submitted ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${athenaColors.royalBlue} 0%, ${athenaColors.softPurple} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <SendIcon sx={{ color: theme.palette.common.white, fontSize: 28 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: theme.palette.common.white, mb: 1 }}
                >
                  Thank you for your message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: alpha(theme.palette.common.white, 0.65),
                    mb: 3,
                    maxWidth: 360,
                    mx: "auto",
                  }}
                >
                  {t("contact.inquiry_received", "We have received your inquiry and will respond within 1-2 business days.")}
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setSubmitted(false)}
                  sx={{
                    color: athenaColors.periwinkle,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Send another message
                </Button>
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                }}
              >
                <TextField
                  label={t("contact.name", "Name")}
                  value={formData.name}
                  onChange={handleChange("name")}
                  required
                  fullWidth
                  autoComplete="name"
                  sx={textFieldSx}
                />
                <TextField
                  label={t("contact.email", "Email")}
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  required
                  fullWidth
                  autoComplete="email"
                  sx={textFieldSx}
                />
                <TextField
                  label="Message"
                  value={formData.message}
                  onChange={handleChange("message")}
                  required
                  fullWidth
                  multiline
                  rows={5}
                  sx={textFieldSx}
                />
                {errorMsg && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      borderRadius: 1,
                      px: 2,
                      py: 1,
                    }}
                  >
                    {errorMsg}
                  </Typography>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={sending}
                  endIcon={<SendIcon />}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${athenaColors.orange} 0%, ${athenaColors.lightOrange} 100%)`,
                    color: theme.palette.common.white,
                    border: "none",
                    "&:hover": {
                      background: `linear-gradient(135deg, ${athenaColors.lightOrange} 0%, ${athenaColors.orange} 100%)`,
                      transform: "translateY(-1px)",
                    },
                    "&:disabled": {
                      opacity: 0.6,
                    },
                  }}
                >
                  {sending ? t("contact.sending", "Sending...") : t("contact.send_message", "Send Message")}
                </Button>
              </Box>
            )}
          </Box>

          {/* RIGHT: Company info + Map */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 3, md: 4 },
            }}
          >
            {/* Company info card */}
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                background: alpha(theme.palette.common.white, 0.04),
                backdropFilter: "blur(8px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: { xs: "1.2rem", md: "1.4rem" },
                  color: theme.palette.common.white,
                }}
              >
                {t("contact.our_office", "Our office")}
              </Typography>

              <InfoItem
                icon={
                  <LocationIcon
                    sx={{ color: athenaColors.periwinkle, fontSize: 22 }}
                  />
                }
                label={t("contact.address_label", "Address")}
                value={`${COMPANY_INFO.address}\n${COMPANY_INFO.addressLine2}\n${COMPANY_INFO.city}`}
                href={`https://maps.google.com/?q=${encodeURIComponent(
                  COMPANY_INFO.address + " " + COMPANY_INFO.city
                )}`}
              />
              <InfoItem
                icon={
                  <PhoneIcon
                    sx={{ color: athenaColors.periwinkle, fontSize: 22 }}
                  />
                }
                label={t("contact.telephone_label", "Telephone")}
                value={COMPANY_INFO.phone}
                href={`tel:${COMPANY_INFO.phone.replace(/\s/g, "")}`}
              />
              <InfoItem
                icon={
                  <EmailIcon
                    sx={{ color: athenaColors.periwinkle, fontSize: 22 }}
                  />
                }
                label="Email"
                value={COMPANY_INFO.email}
                href={`mailto:${COMPANY_INFO.email}`}
              />
              <InfoItem
                icon={
                  <LinkedInIcon
                    sx={{ color: athenaColors.periwinkle, fontSize: 22 }}
                  />
                }
                label={t("contact.linkedin_label", "LinkedIn")}
                value="Athena Tech AI"
                href={COMPANY_INFO.linkedin}
              />
            </Box>

            {/* Map embed */}
            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 250, md: 280 },
                borderRadius: 4,
                overflow: "hidden",
                border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
              }}
            >
              <iframe
                title="Athena Tech Office Location"
                src={MAPS_EMBED_URL}
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  minHeight: isMobile ? 250 : 280,
                  filter:
                    "invert(90%) hue-rotate(180deg) brightness(0.95) contrast(0.9)",
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <LandingFooter />
    </Box>
  );
}
